import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import Button from './Button';
import Input from './Input';

const DialogContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useConfirm must be used inside a <DialogProvider>');
  return ctx.confirm;
};

export const usePrompt = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('usePrompt must be used inside a <DialogProvider>');
  return ctx.prompt;
};

export const DialogProvider = ({ children }) => {
  const [state, setState] = useState(null); // { type, title, message, ... , value }
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState({ type: 'confirm', ...opts });
  }), []);

  const prompt = useCallback((opts) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState({ type: 'prompt', value: opts?.defaultValue ?? '', ...opts });
  }), []);

  // ESC closes (acts like cancel)
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close(state.type === 'prompt' ? null : false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {state && (
        <DialogModal
          state={state}
          setState={setState}
          onCancel={() => close(state.type === 'prompt' ? null : false)}
          onConfirm={() => close(state.type === 'prompt' ? state.value : true)}
        />
      )}
    </DialogContext.Provider>
  );
};

const DialogModal = ({ state, setState, onCancel, onConfirm }) => {
  const isDanger = state.variant === 'danger';
  const inputRef = useRef(null);

  useEffect(() => {
    if (state.type === 'prompt' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [state.type]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg shadow-card-hover max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isDanger ? 'bg-danger-100' : 'bg-primary-100'
              }`}
            >
              {isDanger ? (
                <AlertTriangle size={20} className="text-danger-600" />
              ) : (
                <HelpCircle size={20} className="text-primary-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-neutral-900">
                {state.title || (state.type === 'prompt' ? 'Enter a value' : 'Confirm')}
              </h3>
              {state.message && (
                <p className="mt-1 text-sm text-neutral-600">{state.message}</p>
              )}
              {state.type === 'prompt' && (
                <div className="mt-3">
                  <Input
                    ref={inputRef}
                    type={state.inputType || 'text'}
                    placeholder={state.placeholder || ''}
                    value={state.value}
                    onChange={(e) =>
                      setState((s) => ({ ...s, value: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onConfirm();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 bg-neutral-50 rounded-b-lg flex justify-end gap-2 border-t border-neutral-200">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            {state.cancelText || 'Cancel'}
          </Button>
          <Button variant={isDanger ? 'danger' : 'primary'} size="sm" onClick={onConfirm}>
            {state.confirmText || (state.type === 'prompt' ? 'OK' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};
